import React, {FC, HTMLAttributes, SyntheticEvent, useMemo} from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {OptionsProp} from '../../../../constants/types';
import AutocompleteInputMenu, {SelectWithNewEntity} from './AutocompleteInputMenu';

export type SelectRedirectProps = { path: string, from?: string };

export interface AutocompleteInputProps {
  id: string,
  name: string,
  label?: string,
  options: OptionsProp[],
  disabled?: boolean,
  disableClearable?: boolean,
  disableCloseOnSelect?: boolean,
  required?: boolean,
  value: Nullable<string>,
  error?: string,
  touched?: boolean,
  multiple?: boolean,
  disablePortal?: boolean,
  inputValue?: string,
  maxMenuHeight?: number,
  className?: string,
  testId?: string,
  apiValidationError?: Nullable<Dictionary<string[]>>,
  onChange: (
    event: SyntheticEvent<Element, Event>,
    value: Nullable<OptionsProp | OptionsProp[]>
  ) => void,
  onInputChange?: (
    event: SyntheticEvent<Element, Event>,
    value: string
  ) => void,
  onFocus?: () => void,
  onBlur?: () => void,
  size?: 'small' | 'medium',
  withNewEntity?: SelectWithNewEntity,
  loading?: boolean
}

const getMultiSelectValue = (options: OptionsProp[], value) =>
  options?.filter((option) => value?.includes(option.value)) || [];

const getSingleSelectValue = (options: OptionsProp[], value) =>
  options?.find((option) => option.value === value) || null;

const AutocompleteInput: FC<AutocompleteInputProps> = ({
  id,
  name,
  label,
  options,
  disabled = false,
  required = false,
  disableClearable = true,
  disableCloseOnSelect = false,
  value,
  error,
  touched,
  inputValue,
  onChange,
  onInputChange,
  onFocus,
  onBlur,
  size = 'medium',
  className,
  apiValidationError,
  multiple,
  withNewEntity,
  loading,
  disablePortal = true,
  maxMenuHeight
}) => {
  const currentValue = useMemo(() => {
    if (!multiple) {
      return getSingleSelectValue(options, value);
    }

    return getMultiSelectValue(options, value);
  }, [options, value]);

  const hasErrors = !!apiValidationError || !!(touched && error);
  const errorMessage = apiValidationError?.[name] || (touched && error);

  const customProps = {
    ...(!!withNewEntity && {
      PaperComponent: ({children, className}: HTMLAttributes<HTMLElement>) => (
        <AutocompleteInputMenu
          withNewEntity={withNewEntity}
          className={className}
        >
          {children}
        </AutocompleteInputMenu>
      )})
  };

  return (
    <Autocomplete
      className={className}
      id={id}
      value={currentValue}
      inputValue={inputValue}
      options={options}
      disabled={disabled}
      disableClearable={disableClearable}
      onChange={onChange}
      onInputChange={onInputChange}
      onFocus={onFocus}
      onBlur={onBlur}
      multiple={multiple}
      loading={loading}
      disableCloseOnSelect={disableCloseOnSelect}
      disablePortal={disablePortal}
      size={size}
      ListboxProps={{style: {maxHeight: maxMenuHeight}}}
      {...customProps}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size={size}
          name={name}
          label={label}
          required={required}
          error={hasErrors}
          helperText={errorMessage}
          inputProps={{
            ...params?.inputProps,
            'data-testid': id
          }}
        />
      )}
    />
  );
};

export default AutocompleteInput;
